import { Button, HStack, Image, Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { OrderProductImage, OrderProductInfo, type OrderProductProps } from '@/shared/components/OrderProduct';
import { getRequestContext, type RequestContext } from '@/shared/lib/requestContext';
import removeIconSource from './assets/closeIcon.svg';
import minusIconSource from './assets/minusIcon.svg';
import plusIconSource from './assets/plusIcon.svg';
import { OrderVariantsEdit } from './components/OrderVariantsEdit';
import { useProductQuantity } from './lib/useProductQuantity';
import styles from './OrderProduct.module.css';

export interface OrderProductUIProps extends Omit<OrderProductProps, 'isExpressCheckout' | 'reinitialize'>, TestProps {
    isExpressCheckout: RequestContext['isExpressCheckout'];
    isDisableEditOrderItems: RequestContext['delivery']['disableEditOrderItems'];
    quantity: number;
    updateQuantity: (quantity: number) => void;
    clearQuantity: VoidFunction;
}

export const OrderProductUI = (props: OrderProductUIProps) => {
    const {
        'data-testid': dataTestId = 'OrderProduct',
        name,
        caption,
        image,
        quantity,
        maxQuantity,
        price,
        finalPrice,
        canRemoved,
        url,
        characteristics,
        variantId,
        chunkId,
        isExpressCheckout,
        isDisableEditOrderItems,
        updateQuantity,
        clearQuantity,
    } = props;

    return (
        <HStack gap="20" alignItems="stretch" className={styles.root} data-testid={dataTestId}>
            <OrderProductImage
                image={image}
                url={url}
                name={name}
                isExpressCheckout={isExpressCheckout}
                data-testid={`${dataTestId}.Image`}
            />

            <div className={styles.content}>
                <VStack
                    justifyContent={isDisableEditOrderItems ? 'flex-start' : 'space-between'}
                    className={styles.wrapper}
                    gap="12"
                >
                    <OrderProductInfo
                        name={name}
                        url={url}
                        caption={caption}
                        price={price}
                        finalPrice={finalPrice}
                        quantity={quantity}
                        isExpressCheckout={isExpressCheckout}
                        data-testid={`${dataTestId}.Info`}
                        variantsInfo={
                            <OrderVariantsEdit
                                characteristics={characteristics}
                                variantId={variantId}
                                chunkId={chunkId}
                            />
                        }
                    />

                    {isDisableEditOrderItems ? null : (
                        <HStack justifyContent={canRemoved ? 'space-between' : 'flex-end'}>
                            {canRemoved && (
                                <Button
                                    className={styles.removeButton}
                                    size="s"
                                    color="contour"
                                    onClick={clearQuantity}
                                    data-testid={`${dataTestId}.RemoveButton`}
                                >
                                    <Image width="16" height="16" src={removeIconSource} alt="Удалить" />
                                </Button>
                            )}
                            <HStack
                                gap="24"
                                alignItems="center"
                                className={styles.quantityActions}
                                data-testid={`${dataTestId}.QuantityActions`}
                            >
                                <Button
                                    className={styles.editQuantityButton}
                                    size="s"
                                    color="contrast"
                                    onClick={() => updateQuantity(quantity - 1)}
                                    isDisabled={quantity === 1}
                                    data-testid={`${dataTestId}.DecreaseButton`}
                                >
                                    <Image
                                        className={quantity === 1 ? styles.actionDisabled : styles.actionEnabled}
                                        width="16"
                                        height="16"
                                        src={minusIconSource}
                                        alt="Удалить"
                                    />
                                </Button>
                                <Text size="mc" data-testid={`${dataTestId}.Quantity`}>
                                    {quantity}
                                </Text>
                                <Button
                                    className={styles.editQuantityButton}
                                    size="s"
                                    color="contrast"
                                    onClick={() => updateQuantity(quantity + 1)}
                                    isDisabled={quantity === maxQuantity}
                                    data-testid={`${dataTestId}.IncreaseButton`}
                                >
                                    <Image
                                        className={
                                            quantity === maxQuantity ? styles.actionDisabled : styles.actionEnabled
                                        }
                                        width="16"
                                        height="16"
                                        src={plusIconSource}
                                        alt="Добавить"
                                    />
                                </Button>
                            </HStack>
                        </HStack>
                    )}
                </VStack>
            </div>
        </HStack>
    );
};

export const OrderProduct = (props: Omit<OrderProductProps, 'isExpressCheckout'>) => {
    const { quantity, chunkId, variantId, reinitialize, ...restProps } = props;
    const {
        isExpressCheckout,
        delivery: { disableEditOrderItems },
    } = getRequestContext();

    const { currentQuantity, updateQuantity, clearQuantity } = useProductQuantity({
        quantity,
        chunkId,
        variantId,
        reinitialize,
    });

    return (
        <OrderProductUI
            {...restProps}
            chunkId={chunkId}
            variantId={variantId}
            quantity={currentQuantity}
            updateQuantity={updateQuantity}
            clearQuantity={clearQuantity}
            isDisableEditOrderItems={disableEditOrderItems}
            isExpressCheckout={isExpressCheckout}
        />
    );
};
